import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, Observable, share } from 'rxjs';
import { ProfileService } from '../services/profile.service';

/**
 * Revalidation state shared across all requests processed by this interceptor.
 * When a 403 triggers cookie refresh, concurrent 403'd requests share
 * the same revalidation Observable to avoid parallel refresh calls.
 */
let revalidation$: Observable<unknown> | null = null;

export const protectedResourceInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/protected/')) {
    return next(req);
  }

  const profile = inject(ProfileService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 403) {
        return throwError(() => error);
      }

      // Cookie revalidation: serialize concurrent refreshes
      if (!revalidation$) {
        revalidation$ = profile.fetchProtectedCookies().pipe(
          share({
            resetOnComplete: true,
            resetOnError: true,
            resetOnRefCountZero: true,
          }),
        );

        // Clear reference once the revalidation completes or errors
        revalidation$.subscribe({
          complete: () => (revalidation$ = null),
          error: () => (revalidation$ = null),
        });
      }

      return revalidation$.pipe(
        switchMap(() =>
          // Retry the original request once after fresh cookies
          next(req),
        ),
        catchError(() =>
          // Revalidation or retry failed — propagate original error, no further retry
          throwError(() => error),
        ),
      );
    }),
  );
};
